import { Button, FieldWrapper, FlexBox, Text } from '@dixa/lib-shared-habla';
import { HookFormText } from 'lib-shared-hook-form';
import { useFormContext } from 'react-hook-form';

import { PartnerSettingsConfigurationSchema } from 'apis/partnerIntegrations';

type ThirdPartySettingsConfigurationSchemaFieldProps = {
  field: PartnerSettingsConfigurationSchema;
  isCreateMode: boolean;
};

/**
 * A component for rendering a schema field input depending on the field type.
 *
 * @param field - The field to render.
 * @param isCreateMode - Whether the field is being created or updated.
 * @returns An input field depending on the field type.
 */
export const ThirdPartySettingsConfigurationSchemaField = ({
  field,
  isCreateMode,
}: ThirdPartySettingsConfigurationSchemaFieldProps): JSX.Element => {
  const form = useFormContext();
  const formField = form.watch(field.key);

  const {
    label,
    key,
    type,
    prefix,
    required,
    readOnly,
    placeholder,
    secret: isSecret,
  } = field;

  switch (type) {
    // A basic text input field.
    // Secret values never come back to the frontend. In case of a secret value
    // we show a button that enables the user to update the value. Otherwise we
    // will ignore the input field in the form.
    case 'input':
      return formField.isEditing || isCreateMode ? (
        <HookFormText
          label={label}
          name={`${key}.value`}
          rules={{
            required: required ? `${label} is required` : undefined,
          }}
          componentProps={{
            placeholder: placeholder ?? '',
            readOnly: readOnly ?? false,
            ...(prefix && {
              prefix: {
                type: 'custom',
                value: prefix,
              },
            }),
            ...(isSecret &&
              !isCreateMode && {
                prefix: {
                  type: 'button',
                  button: {
                    icon: 'close',
                    onClick: () => form.setValue(`${key}.isEditing`, false),
                  },
                },
              }),
          }}
        />
      ) : (
        <FieldWrapper label={label}>
          <Button
            variant="secondary"
            icon="pen"
            onClick={() => form.setValue(`${key}.isEditing`, true)}
          >
            Update {label}
          </Button>
        </FieldWrapper>
      );
    default:
      return (
        <FlexBox>
          <Text.Paragraph>Unsupported input type: {type}</Text.Paragraph>
        </FlexBox>
      );
  }
};
